// Единая точка чтения прогресса для карты и guard'а /play (client-design §5.4):
// залогинен — сервер (источник правды), гость — локальный сейв dg_save_v1.
// Фейл сети у залогиненного → локальный блоб (он же кэш после клейма, §5.2),
// чтобы карта не запиралась из-за моргнувшего API.

import { routes } from '@/api/routes';
import { authorizedFetch, authStore } from '@/auth/authStore';
import { loadSave } from '@/save/saveStore';

export interface ProgressEntry { rating: number; status: string }
export type ProgressMap = Record<string, ProgressEntry>;

interface ServerProgress { levels: { levelId: string; rating: number; status: string }[] }

function localProgress(): ProgressMap {
  return Object.fromEntries(
    Object.entries(loadSave().levels)
      .map(([id, e]) => [id, { rating: e.rating, status: e.status }]),
  );
}

export async function loadProgressMap(): Promise<ProgressMap> {
  const { status } = authStore.get();
  if (status !== 'authed' || !authStore.accessToken) return localProgress();
  try {
    // Bearer + авто-refresh протухшего токена — внутри authorizedFetch
    const res = await authorizedFetch(routes.meProgress, { credentials: 'include' });
    if (!res.ok) return localProgress();
    const body = (await res.json()) as ServerProgress;
    return Object.fromEntries(
      body.levels.map((l) => [l.levelId, { rating: l.rating, status: l.status }]),
    );
  } catch {
    return localProgress();
  }
}

// --- Очередь неотправленных результатов (dg_pending_v1): фейл POST /result
// не должен терять прогресс. Кладём ДО отправки, удаляем по resp.ok. ---

interface PendingResult { levelId: string; body: unknown }

const PENDING_KEY = 'dg_pending_v1';

function readPending(): PendingResult[] {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY) ?? '[]') as PendingResult[]; }
  catch { return []; }
}

function writePending(items: PendingResult[]): void {
  try {
    if (items.length) localStorage.setItem(PENDING_KEY, JSON.stringify(items));
    else localStorage.removeItem(PENDING_KEY);
  } catch { /* private mode: очередь только в этой сессии */ }
}

let flushChain: Promise<void> = Promise.resolve(); // флаши сериализуются

/** Флаш очереди по порядку: ok → удаляем; 4xx (кроме 401) — сервер отверг,
 *  ретрай бессмысленен, тоже удаляем; сеть/5xx/401 — стоп до следующего раза.
 *  Зовётся на старте приложения и перед гейтом /play. Никогда не бросает. */
export function flushPendingResults(): Promise<void> {
  flushChain = flushChain.then(async () => {
    while (authStore.get().status === 'authed' && readPending().length) {
      const head = readPending()[0];
      let res: Response;
      try {
        res = await authorizedFetch(routes.levelResult(head.levelId), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(head.body),
        });
      } catch { return; } // сеть упала — результат остаётся в очереди
      if (!res.ok && (res.status === 401 || res.status >= 500)) return;
      writePending(readPending().slice(1)); // доехал (или отвергнут 4xx)
    }
  });
  return flushChain;
}

/** POST результата уровня через очередь: сначала в dg_pending_v1, потом флаш. */
export function postLevelResult(levelId: string, body: unknown): Promise<void> {
  writePending([...readPending(), { levelId, body }]);
  return flushPendingResults();
}
